import { memo, useEffect, useMemo, useRef } from 'react';
import { Plane, Vector2Tuple, Vector3 } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useDollhouse } from '@spazious/dollhouse-engine';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useStaging } from '@spazious/dollhouse-store';
import { useEventCallback } from '@spazious/react-hooks';

export type PolygonDrawControlsProps =
  | {
      type: FShallowNodeTypesEnum.FArea;
    }
  | {
      type: FShallowNodeTypesEnum.FPolygon;
      id: number;
    };

export const PolygonDrawControls: React.FC<PolygonDrawControlsProps> = memo((props) => {
  const selectorRef = useRef<string | null>(null);
  const { staging, createAction } = useStaging();

  const camera = useDollhouse(({ camera }) => camera);
  const pointer = useDollhouse(({ pointer }) => pointer);
  const raycaster = useDollhouse(({ raycaster }) => raycaster);
  const events = useDollhouse(({ events }) => events);
  const plane = useMemo(() => new Plane(camera.up, 0), [camera.up]);

  const points = useRef<Vector2Tuple[]>([]);

  const getPoint = useEventCallback(() => {
    const point = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, point);
    return [point.x, point.z] as [number, number];
  });

  const elem = events.connected as HTMLElement | undefined;

  useEffect(() => {
    const handlePointerDown = () => {
      if (selectorRef.current) {
        points.current.push(getPoint());
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
          ])
        );
      } else {
        const action = createAction('add', {
          ...props,
          points: [],
        });
        selectorRef.current = action.payload.selector;
        staging.add(action);
        points.current.push(getPoint());
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
          ])
        );
      }
    };

    const handlePointerMove = () => {
      if (selectorRef.current) {
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
            getPoint(),
          ])
        );
      }
    };

    const handlePointerUp = () => {
      if (selectorRef.current && staging.stack.length && points.current.length > 2) {
        staging.commit(
          staging.stack.length > 1 ? CommanderActionTypesEnum.Add : CommanderActionTypesEnum.Update
        );
      }
    };

    elem?.addEventListener('pointermove', handlePointerMove);
    elem?.addEventListener('pointerdown', handlePointerDown);
    elem?.addEventListener('pointerup', handlePointerUp);

    return () => {
      elem?.removeEventListener('pointerdown', handlePointerDown);
      elem?.removeEventListener('pointermove', handlePointerMove);
      elem?.removeEventListener('pointerup', handlePointerUp);
      staging.abort();
    };
  }, [createAction, elem, getPoint, staging, props]);

  return null;
});
